import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { Pagination } from 'src/app/classes/pagination';
import { LANG } from 'src/app/models/enums/lang.enum';
import { HospitalService } from 'src/app/services/hospital.service';

export enum HOSPITAL_CONTRACT_TYPE {
	PARTNER = 'PARTNER',
	NON_PARTNER = 'NON_PARTNER',
	OTHER = 'OTHER',
}

export enum CONSUMPTION_TYPE {
	MATERNITY = 'MATERNITY',
	FUNERAL = 'FUNERAL',
	EMERGENCY = 'EMERGENCY',
	SICK_LEAVE = 'SICK_LEAVE',
	OTHER = 'OTHER',
}

export interface ConsumptionInfoCorrectionDialogData {
	type?: CONSUMPTION_TYPE | string;
	title?: string;
	start_date?: string | Date | null;
	end_date?: string | Date | null;
	is_contract_hospital?: string | null;
	contract_type?: string | null;
	hospital?: any;
	hospital_data?: any;
	hospital_id?: string;
	dead_date?: string | Date | null;
	death_date?: string | Date | null;
	delivery_date?: string | Date | null;
	give_birth_date?: string | Date | null;
	patient?: any;
	data?: any;
}

@Component({
	selector: 'app-consumption-info-correction-dialog',
	templateUrl: './consumption-info-correction-dialog.component.html',
	styleUrls: ['./consumption-info-correction-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionInfoCorrectionDialogComponent implements OnInit {
	lang: LANG;
	readonly LANG = LANG;
	readonly HOSPITAL_CONTRACT_TYPE = HOSPITAL_CONTRACT_TYPE;

	form: FormGroup;
	hospitals: any[] = [];
	selectedHospital: any = null;
	hospitalPage: Pagination = new Pagination(1, 50, 0);
	hospitalSearchString: string = '';

	showContractTypeNull: boolean = false;
	showContractTypeAll: boolean = false;
	showContractTypePartner: boolean = false;
	showContractTypeNonPartner: boolean = false;

	constructor(
		private dialogRef: MatDialogRef<ConsumptionInfoCorrectionDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ConsumptionInfoCorrectionDialogData,
		private hospitalService: HospitalService,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef,
	) {
		this.lang = this.translate.currentLang as LANG;

		const initialStartDate = this.data?.start_date ?? this.data?.patient?.start_date ?? null;
		const initialEndDate = this.data?.end_date ?? this.data?.patient?.end_date ?? null;

		let initialHospitalObj: any = null;
		if (this.data?.hospital && typeof this.data.hospital === 'object' && this.data.hospital._id) {
			initialHospitalObj = this.data.hospital;
		} else if (this.data?.patient?.hospital && typeof this.data.patient.hospital === 'object' && this.data.patient.hospital._id) {
			initialHospitalObj = this.data.patient.hospital;
		} else if (this.data?.hospital_data && typeof this.data.hospital_data === 'object' && this.data.hospital_data._id) {
			initialHospitalObj = this.data.hospital_data;
		}

		const initialHospitalId =
			initialHospitalObj?._id ||
			(typeof this.data?.hospital === 'string' ? this.data.hospital : null) ||
			(typeof this.data?.patient?.hospital === 'string' ? this.data.patient.hospital : null) ||
			this.data?.patient?.hospital_id ||
			this.data?.hospital_id ||
			null;

		let initialIsContractHospital =
			this.data?.is_contract_hospital ??
			this.data?.contract_type ??
			this.data?.patient?.is_contract_hospital ??
			this.data?.patient?.contract_type ??
			null;

		if (this.isEmergency) {
			initialIsContractHospital = HOSPITAL_CONTRACT_TYPE.NON_PARTNER;
		} else if (this.isSickLeave) {
			initialIsContractHospital = HOSPITAL_CONTRACT_TYPE.PARTNER;
		} else if (this.isMaternity && initialIsContractHospital === null) {
			initialIsContractHospital = 'ALL';
		}

		if (initialIsContractHospital === null && initialHospitalObj) {
			if (initialHospitalObj.contract_type === HOSPITAL_CONTRACT_TYPE.PARTNER) {
				initialIsContractHospital = HOSPITAL_CONTRACT_TYPE.PARTNER;
			} else if (initialHospitalObj.contract_type === HOSPITAL_CONTRACT_TYPE.NON_PARTNER) {
				initialIsContractHospital = HOSPITAL_CONTRACT_TYPE.NON_PARTNER;
			}
		}

		const initialDeliveryDate =
			this.data?.delivery_date ??
			this.data?.give_birth_date ??
			this.data?.patient?.give_birth_date ??
			this.data?.patient?.delivery_date ??
			null;
		const initialDeadDate =
			this.data?.dead_date ??
			this.data?.death_date ??
			this.data?.patient?.death_date ??
			this.data?.patient?.dead_date ??
			null;

		// Preload initial hospital object if provided
		if (initialHospitalObj && initialHospitalObj._id) {
			this.selectedHospital = initialHospitalObj;
			this.hospitals = [initialHospitalObj];
		}

		this.form = new FormGroup({
			start_date: new FormControl(initialStartDate),
			end_date: new FormControl(initialEndDate),
			is_contract_hospital: new FormControl(initialIsContractHospital),
			hospital: new FormControl(initialHospitalId),
			delivery_date: new FormControl(initialDeliveryDate),
			dead_date: new FormControl(initialDeadDate),
		});

		if (initialHospitalId && (!initialHospitalObj || !initialHospitalObj.name_kh)) {
			this.fetchHospitalById(initialHospitalId);
		}
	}

	ngOnInit(): void {
		this.updateContractTypeVisibility();
		if (this.form.get('is_contract_hospital')?.value !== null) {
			this.getHospitals();
		}
	}

	updateContractTypeVisibility(): void {
		if (this.isSickLeave) {
			this.showContractTypeNull = false;
			this.showContractTypeAll = false;
			this.showContractTypePartner = true;
			this.showContractTypeNonPartner = false;
		} else if (this.isEmergency) {
			this.showContractTypeNull = false;
			this.showContractTypeAll = false;
			this.showContractTypePartner = false;
			this.showContractTypeNonPartner = true;
		} else if (this.isMaternity) {
			this.showContractTypeNull = false;
			this.showContractTypeAll = true;
			this.showContractTypePartner = true;
			this.showContractTypeNonPartner = true;
		} else {
			this.showContractTypeNull = true;
			this.showContractTypeAll = true;
			this.showContractTypePartner = true;
			this.showContractTypeNonPartner = true;
		}
		this.cdr.markForCheck();
	}

	async fetchHospitalById(hospitalId: string) {
		try {
			const res: any = await lastValueFrom(this.hospitalService.getHospitalById(hospitalId));
			if (res?.data) {
				this.selectedHospital = res.data;
				const exists = this.hospitals.some(h => h._id === res.data._id);
				if (!exists) {
					this.hospitals = [res.data, ...this.hospitals];
				} else {
					const index = this.hospitals.findIndex(h => h._id === res.data._id);
					this.hospitals[index] = res.data;
				}
				if (
					this.form.get('is_contract_hospital')?.value === null &&
					res.data.contract_type
				) {
					this.form.get('is_contract_hospital')?.setValue(res.data.contract_type);
				}
				this.cdr.markForCheck();
			}
		} catch (err) {
			console.warn('Could not fetch hospital by ID:', err);
		}
	}

	get isMaternity(): boolean {
		const type = (this.data?.type || '').toUpperCase();
		return type === 'MATERNITY' || type === 'ML' || type === 'MATERNITY_LEAVE' || type === '2';
	}

	get isSickLeave(): boolean {
		const type = (this.data?.type || '').toUpperCase();
		return type === 'SICK_LEAVE' || type === 'SL' || type === 'SICKLEAVE' || type === '3';
	}

	get isEmergency(): boolean {
		const type = (this.data?.type || '').toUpperCase();
		return type === 'EMERGENCY' || type === 'EM' || type === '1';
	}

	get isFuneral(): boolean {
		const type = (this.data?.type || '').toUpperCase();
		return type === 'FUNERAL' || type === 'SURVIVAL' || type === 'DEATH' || type === 'ALLOWANCE' || type === '4';
	}

	onContractTypeChange(value: any): void {
		this.form.controls['hospital']?.setValue(null);
		this.hospitals = [];
		this.hospitalPage.page = 1;

		if (value === null || value === HOSPITAL_CONTRACT_TYPE.OTHER) {
			this.form.controls['hospital'].clearValidators();
		} else {
			this.form.controls['hospital'].setValidators(Validators.required);
			this.getHospitals(this.hospitalSearchString);
		}
		this.form.controls['hospital'].updateValueAndValidity();
		this.cdr.markForCheck();
	}

	getHospitals(search: string = '') {
		const isContractHospital = this.form.get('is_contract_hospital')?.value;
		let contract_type: string | undefined = undefined;

		if (isContractHospital === HOSPITAL_CONTRACT_TYPE.PARTNER) {
			contract_type = HOSPITAL_CONTRACT_TYPE.PARTNER;
		} else if (isContractHospital === HOSPITAL_CONTRACT_TYPE.NON_PARTNER) {
			contract_type = HOSPITAL_CONTRACT_TYPE.NON_PARTNER;
		} else if (isContractHospital === 'ALL') {
			contract_type = undefined;
		}

		this.hospitalService
			.getHospitals({
				page: this.hospitalPage.page,
				count: this.hospitalPage.count,
				search,
				contract_type,
			})
			.subscribe((res: any) => {
				if (!res?.data) return;
				if (this.hospitalPage.page === 1) {
					// Always preserve selected/initial hospital so it remains selectable regardless of pagination
					const currentSelectedId = this.form.get('hospital')?.value || this.selectedHospital?._id;
					const selectedHospitalItem = this.selectedHospital || this.hospitals.find(h => h._id === currentSelectedId);
					const existingSelected = selectedHospitalItem && !res.data.some((d: any) => d._id === selectedHospitalItem._id)
						? [selectedHospitalItem]
						: [];
					this.hospitals = [...existingSelected, ...res.data];
				} else {
					const newItems = res.data.filter((d: any) => !this.hospitals.some((h: any) => h._id === d._id));
					this.hospitals = [...this.hospitals, ...newItems];
				}
				this.hospitalPage = res.pagination || this.hospitalPage;
				this.cdr.markForCheck();
			});
	}

	onHospitalSearch(search: string) {
		this.hospitalSearchString = search || '';
		this.hospitalPage.page = 1;
		this.getHospitals(this.hospitalSearchString);
	}

	onScrollHospital(event: any) {
		if (this.hospitals.length < (this.hospitalPage.total || 0)) {
			this.hospitalPage.page++;
			this.getHospitals(this.hospitalSearchString);
		}
	}

	onCancel(): void {
		this.dialogRef.close(null);
	}

	onSave(): void {
		if (this.form.invalid) return;
		const rawValue = this.form.getRawValue();
		const selectedHospital = this.hospitals.find(h => h._id === rawValue.hospital) || this.selectedHospital;
		this.dialogRef.close({
			start_date: rawValue.start_date,
			end_date: rawValue.end_date,
			is_contract_hospital: rawValue.is_contract_hospital,
			contract_type: rawValue.is_contract_hospital === 'ALL' ? null : rawValue.is_contract_hospital,
			hospital: rawValue.hospital,
			hospital_data: selectedHospital,
			...(this.isMaternity
				? { delivery_date: rawValue.delivery_date, give_birth_date: rawValue.delivery_date }
				: {}),
			...(this.isFuneral
				? { dead_date: rawValue.dead_date, death_date: rawValue.dead_date }
				: {}),
		});
	}
}
